import type { WizardStepProps } from '../../../../types/builder.types'
import { getAggregatableFields, getAllFields, AGGREGATION_TYPES } from '../../../../config/builder-schemas'
import { useDataSources } from '../../../../hooks/useDataSources'
import { useMemo } from 'react'
import {
  fieldsFromCustomQuery,
  getCustomQueryConfigSignature,
} from '../../../../utils/custom-query-builder-fields'

const PIE_COLORS = [
  { name: 'Bosque', colors: ['#10b981', '#059669', '#047857', '#065f46', '#064e3b'] },
  { name: 'Océano', colors: ['#3b82f6', '#2563eb', '#1d4ed8', '#1e40af', '#1e3a8a'] },
  { name: 'Atardecer', colors: ['#f59e0b', '#f97316', '#ef4444', '#dc2626', '#b91c1c'] },
  { name: 'Tierra', colors: ['#a16207', '#854d0e', '#713f12', '#78350f', '#451a03'] },
]

export function PieChartConfiguration({ config, onChange, onNext, onPrev }: WizardStepProps) {
  const { dataSources } = useDataSources()

  const cqSig = useMemo(
    () => getCustomQueryConfigSignature(config.customQuery),
    [config.customQuery]
  )

  // Get fields from dynamic data sources or fallback to static
  const { aggregatableFields, allFields } = useMemo(() => {
    if (!config.dataSource) return { aggregatableFields: [], allFields: [] }

    if (
      (config.dataSource === 'custom_query' ||
        (config.dataSource?.includes('ckan_data_generic') && config.customQuery)) &&
      config.customQuery?.config?.selectedFields?.length
    ) {
      const fieldsArray = fieldsFromCustomQuery(config.customQuery)
      return {
        aggregatableFields: fieldsArray,
        allFields: fieldsArray,
      }
    }

    // First try to find in dynamic sources (includes both ETL and CKAN)
    const dynamicSource = dataSources.find(ds => ds.tableName === config.dataSource)
    if (dynamicSource) {
      return {
        aggregatableFields: dynamicSource.fields.filter(f => f.aggregatable),
        allFields: dynamicSource.fields
      }
    }

    // Fallback to static function for backwards compatibility
    return {
      aggregatableFields: getAggregatableFields(config.dataSource),
      allFields: getAllFields(config.dataSource)
    }
  }, [dataSources, config.dataSource, cqSig])

  const categoryFields = allFields.filter(f => f.type === 'string' || !f.aggregatable)

  const pieConfig = config.pieConfig || { dataKey: '', nameKey: '', showPercentage: true, innerRadius: 0 }

  const isValid = Boolean(
    config.metadata?.title &&
    pieConfig.dataKey &&
    pieConfig.nameKey &&
    config.aggregation
  )

  return (
    <div className="max-w-3xl mx-auto">
      <button onClick={onPrev} className="mb-6 text-green-600 hover:underline">
        ← Volver
      </button>

      <h2 className="text-2xl font-bold mb-2">Configurar Gráfico Circular</h2>
      <p className="text-gray-600 mb-6">
        Muestra distribución porcentual de categorías (pie o donut)
      </p>

      <div className="space-y-6 bg-white border rounded-lg p-6">
        {/* Título */}
        <div>
          <label className="block font-medium mb-2">
            Título del Gráfico <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            placeholder="Ej: Distribución de Especies Forestales"
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={config.metadata?.title || ''}
            onChange={(e) =>
              onChange({
                ...config,
                metadata: { ...config.metadata, title: e.target.value },
              })
            }
          />
        </div>

        {/* Campo de Categorías (nameKey) */}
        <div>
          <label className="block font-medium mb-2">
            Campo de Categorías <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={pieConfig.nameKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                pieConfig: { ...pieConfig, nameKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {categoryFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Campo con las categorías o nombres (ej: especies, provincias)
          </p>
        </div>

        {/* Campo Numérico (dataKey) */}
        <div>
          <label className="block font-medium mb-2">
            Campo Numérico <span className="text-red-500">*</span>
          </label>
          <select
            className="w-full border rounded px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500"
            value={pieConfig.dataKey || ''}
            onChange={(e) =>
              onChange({
                ...config,
                pieConfig: { ...pieConfig, dataKey: e.target.value },
              })
            }
          >
            <option value="">Selecciona un campo...</option>
            {aggregatableFields.map((field) => (
              <option key={field.name} value={field.name}>
                {field.label}
              </option>
            ))}
          </select>
          <p className="text-sm text-gray-500 mt-1">
            Campo numérico que se sumará/promediará por categoría
          </p>
        </div>

        {/* Agregación */}
        <div>
          <label className="block font-medium mb-2">
            Operación <span className="text-red-500">*</span>
          </label>
          <div className="grid grid-cols-5 gap-3">
            {AGGREGATION_TYPES.map((agg) => (
              <button
                key={agg.value}
                type="button"
                className={`
                  border-2 rounded p-3 text-center transition-all
                  ${
                    config.aggregation === agg.value
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() => onChange({ ...config, aggregation: agg.value as any })}
              >
                <div className="text-xl mb-1">{agg.icon}</div>
                <div className="text-xs font-medium">{agg.value}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Tipo: Pie vs Donut */}
        <div>
          <label className="block font-medium mb-2">Estilo del Gráfico</label>
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              className={`
                border-2 rounded p-4 transition-all
                ${
                  (pieConfig.innerRadius || 0) === 0
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }
              `}
              onClick={() =>
                onChange({
                  ...config,
                  pieConfig: { ...pieConfig, innerRadius: 0 },
                })
              }
            >
              <div className="text-3xl mb-2">🥧</div>
              <div className="font-medium">Pie Completo</div>
              <div className="text-xs text-gray-500 mt-1">Círculo lleno</div>
            </button>
            <button
              type="button"
              className={`
                border-2 rounded p-4 transition-all
                ${
                  (pieConfig.innerRadius || 0) > 0
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-green-300'
                }
              `}
              onClick={() =>
                onChange({
                  ...config,
                  pieConfig: { ...pieConfig, innerRadius: 60 },
                })
              }
            >
              <div className="text-3xl mb-2">🍩</div>
              <div className="font-medium">Donut</div>
              <div className="text-xs text-gray-500 mt-1">Con agujero central</div>
            </button>
          </div>
        </div>

        {/* Paleta de colores */}
        <div>
          <label className="block font-medium mb-2">Paleta de Colores</label>
          <div className="grid grid-cols-2 gap-3">
            {PIE_COLORS.map((palette) => (
              <button
                key={palette.name}
                type="button"
                className={`
                  border-2 rounded p-3 transition-all
                  ${
                    JSON.stringify(pieConfig.colors) === JSON.stringify(palette.colors)
                      ? 'border-green-500 bg-green-50'
                      : 'border-gray-200 hover:border-green-300'
                  }
                `}
                onClick={() =>
                  onChange({
                    ...config,
                    pieConfig: { ...pieConfig, colors: palette.colors },
                  })
                }
              >
                <div className="flex gap-1 mb-2">
                  {palette.colors.slice(0, 5).map((color, i) => (
                    <div key={i} className="w-full h-6 rounded" style={{ backgroundColor: color }}></div>
                  ))}
                </div>
                <div className="text-sm font-medium">{palette.name}</div>
              </button>
            ))}
          </div>
        </div>

        {/* Mostrar porcentajes */}
        <div className="flex items-center gap-3">
          <input
            type="checkbox"
            id="showPercentage"
            checked={pieConfig.showPercentage !== false}
            onChange={(e) =>
              onChange({
                ...config,
                pieConfig: { ...pieConfig, showPercentage: e.target.checked },
              })
            }
            className="w-5 h-5 text-green-600"
          />
          <label htmlFor="showPercentage" className="font-medium cursor-pointer">
            Mostrar porcentajes en las etiquetas
          </label>
        </div>
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={onPrev}
          className="flex-1 bg-gray-200 text-gray-700 py-3 rounded-lg font-medium hover:bg-gray-300"
        >
          ← Atrás
        </button>
        <button
          onClick={onNext}
          disabled={!isValid}
          className="flex-1 bg-green-600 text-white py-3 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed"
        >
          Vista Previa →
        </button>
      </div>
    </div>
  )
}
